// Naima's housing provider outreach, worked out from her mailbox in Resend (what she sent and what came back)
// and the housing provider list. Plain JavaScript so node can test it without a browser.
//
// A sent email belongs to a housing provider when one of its recipients is an address in the provider's email box, or, failing
// that, is at the same web domain as that box or the provider's website. Free-mail domains (gmail.com...) are never matched by
// domain, because everyone shares them. Our own addresses are ignored. An email to several providers counts for each.

/** Domains everybody shares, so an address there says nothing about which organisation it is. */
export const freeMailDomains = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'hotmail.co.uk', 'live.com', 'live.co.uk',
  'yahoo.com', 'yahoo.co.uk', 'icloud.com', 'me.com', 'aol.com', 'btinternet.com', 'sky.com', 'protonmail.com',
]);

/** Our own mail: never a housing provider. */
const ownDomains = new Set(['residentservice360.bedmaps.com', 'astonmetrics.com']);

const DAY = 86400000;
const ADDRESS = /[a-z0-9._%+'-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)+/g;

/** Every email address in a box that may hold several ("a@x.org; b@y.org", "Pat <pat@x.org>"), lower case, once each. */
export function addressesIn(value) {
  return [...new Set(String(value ?? '').toLowerCase().match(ADDRESS) ?? [])];
}

const domainOf = (address) => address.slice(address.lastIndexOf('@') + 1);
const sharedDomain = (domain) => freeMailDomains.has(domain) || ownDomains.has(domain);

/** "midlandshomes.example" for "https://www.midlandshomes.example/about" or "www.midlandshomes.example"; null when it isn't an address. */
function hostOf(website) {
  const text = String(website ?? '').trim();
  if (!text) return null;
  try {
    const url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`);
    return url.hostname.toLowerCase().replace(/^www\./, '') || null;
  } catch {
    return null;
  }
}

/** Milliseconds for a time as Resend writes it ("2026-10-14 13:05:00", "2026-10-14T13:05:00.123456+00:00", "...+00"); no zone means UTC. NaN when it isn't one. */
function timeOf(value) {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  let text = String(value ?? '').trim().replace(' ', 'T');
  if (!text) return Number.NaN;
  if (/T[\d:.]+[+-]\d{2}$/.test(text)) text += ':00';
  else if (/T[\d:.]+$/.test(text)) text += 'Z';
  return Date.parse(text);
}

/** The London calendar date of a moment, as "2026-10-19". */
const londonDay = (ms) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(ms));

/** What Naima has done so far, by the number of emails she has sent that provider. */
export function stepLabel(sends) {
  if (sends <= 1) return 'First email sent';
  return sends === 2 ? 'Follow-up 1 sent' : 'Follow-up 2 sent';
}

const bounceStatuses = new Set(['bounced', 'complained', 'failed']);
// The partner answers the BedMaps directory lists: taking referrals, or no spaces right now. A provider who declined is not listed.
const directoryStatuses = new Set(['accepted', 'no_capacity']);

/** An email a mail system sent by itself ("Automatic reply: …", "Out of office", "Auto-response"): not a person answering. */
export const isAutomaticReply = (subject) => /\b(automatic\s+reply|auto[\s-]?reply|autoreply|auto[\s-]?respon(se|der)|out\s+of\s+(the\s+)?office)\b/i.test(String(subject ?? ''));

function indexProviders(providers) {
  const byAddress = new Map();
  const byDomain = new Map();
  const add = (map, key, provider) => {
    const list = map.get(key);
    if (!list) map.set(key, [provider]);
    else if (!list.includes(provider)) list.push(provider);
  };
  const withAddress = [];
  for (const provider of Array.isArray(providers) ? providers : []) {
    const addresses = addressesIn(provider?.email);
    if (addresses.length > 0) withAddress.push(provider);
    const domains = new Set(addresses.map(domainOf));
    const host = hostOf(provider?.website);
    if (host) domains.add(host);
    for (const address of addresses) add(byAddress, address, provider);
    for (const domain of domains) if (!sharedDomain(domain)) add(byDomain, domain, provider);
  }
  /** The providers an address belongs to: the ones that list it, else the ones at its domain. */
  const providersOf = (address) => {
    const exact = byAddress.get(address);
    if (exact) return exact;
    const domain = domainOf(address);
    return sharedDomain(domain) ? [] : byDomain.get(domain) ?? [];
  };
  return { providersOf, withAddress };
}

const councilsOf = (provider) => {
  const councils = (Array.isArray(provider.councils) ? provider.councils : []).map((council) => council?.name || council?.code).filter(Boolean);
  return (councils.length > 0 ? councils : (Array.isArray(provider.regions) ? provider.regions : [])).join(', ');
};

/**
 * `{ rows, unmatched, summary }` for the housing providers Naima has emailed.
 *  - `providers`: the housing provider list (`id`, `organisation`, `email`, `website`, `councils`, `regions`).
 *  - `sent` and `received`: her mailbox from Resend, as `{ id, at, to, subject, status }` and `{ id, at, from, subject }`.
 *  - A follow-up is due `followUpDays` after the last email, until the provider replies, an email bounces, or three have been sent.
 * `rows` has one row per provider emailed, with whoever needs a follow-up first, then the ones that replied, then the rest.
 * Each row's `sentOrder` is its place in the order providers were first emailed, from 1.
 * Each row's `onDirectory` says whether the BedMaps directory lists them, from their `partner_status`.
 * Each row's `chain` is the conversation, oldest first: Naima's emails to them and their replies (from their first email on), as
 * `{ id, kind: 'sent' | 'received', at, address, subject }`, so the whole thread can be opened from Resend.
 * An automatic reply ("Automatic reply: …", "Out of office") is in the chain but is not a reply: until a person answers, the
 * provider has not replied, and follow-ups carry on.
 */
export function buildOutreach({ providers = [], sent = [], received = [], now = new Date(), followUpDays = 6 } = {}) {
  const { providersOf, withAddress } = indexProviders(providers);

  // Each provider's emails from us, oldest first, and the emails nobody in the list matches.
  const emailed = new Map();
  const unmatched = [];
  for (const email of Array.isArray(sent) ? sent : []) {
    // Booked for later, or cancelled before it went: not emailed yet.
    if (email?.status === 'scheduled' || email?.status === 'canceled') continue;
    const at = timeOf(email?.at);
    if (Number.isNaN(at)) continue;
    const recipients = [...new Set((Array.isArray(email.to) ? email.to : [email.to]).flatMap(addressesIn))].filter((address) => !ownDomains.has(domainOf(address)));
    if (recipients.length === 0) continue;
    const matches = new Map();
    for (const address of recipients) {
      for (const provider of providersOf(address)) {
        if (!matches.has(provider)) matches.set(provider, address);
      }
    }
    if (matches.size === 0) {
      unmatched.push({ id: email.id, to: recipients, at: new Date(at).toISOString(), subject: email.subject || '' });
      continue;
    }
    for (const [provider, address] of matches) {
      if (!emailed.has(provider)) emailed.set(provider, []);
      emailed.get(provider).push({ id: email.id, at, address, status: email.status || 'sent', subject: email.subject || '' });
    }
  }

  // Each provider's emails back to us.
  const replies = new Map();
  for (const email of Array.isArray(received) ? received : []) {
    const at = timeOf(email?.at);
    if (Number.isNaN(at)) continue;
    for (const address of addressesIn(email.from)) {
      for (const provider of providersOf(address)) {
        if (!replies.has(provider)) replies.set(provider, []);
        replies.get(provider).push({ id: email.id, at, address, subject: email.subject || '' });
      }
    }
  }

  const today = londonDay(timeOf(now));
  const rows = [];
  for (const [provider, emails] of emailed) {
    emails.sort((a, b) => a.at - b.at);
    const first = emails[0];
    const last = emails.at(-1);
    const answers = (replies.get(provider) ?? []).filter((reply) => reply.at >= first.at);
    const replyAt = Math.min(...answers.filter((reply) => !isAutomaticReply(reply.subject)).map((reply) => reply.at), Number.POSITIVE_INFINITY);
    const repliedAt = Number.isFinite(replyAt) ? new Date(replyAt).toISOString() : null;
    const bounced = bounceStatuses.has(last.status);
    const stopped = Boolean(repliedAt) || bounced || emails.length >= 3;
    const nextFollowUp = stopped ? null : new Date(last.at + followUpDays * DAY).toISOString();
    const nextDay = nextFollowUp && londonDay(Date.parse(nextFollowUp));
    rows.push({
      providerId: provider.id,
      organisation: provider.organisation || '',
      onDirectory: directoryStatuses.has(provider.partner_status),
      councils: councilsOf(provider),
      emailedAddress: last.address,
      sends: emails.length,
      firstSentAt: new Date(first.at).toISOString(),
      lastSentAt: new Date(last.at).toISOString(),
      step: stepLabel(emails.length),
      status: repliedAt ? 'replied' : bounced ? 'bounced' : last.status,
      repliedAt,
      nextFollowUp,
      due: !nextDay ? null : nextDay < today ? 'overdue' : nextDay === today ? 'today' : 'later',
      chain: [
        ...emails.map(({ id, at, address, subject }) => ({ id, kind: 'sent', at, address, subject })),
        ...answers.map(({ id, at, address, subject }) => ({ id, kind: 'received', at, address, subject })),
      ].sort((a, b) => a.at - b.at).map((email) => ({ ...email, at: new Date(email.at).toISOString() })),
    });
  }
  [...rows]
    .sort((a, b) => Date.parse(a.firstSentAt) - Date.parse(b.firstSentAt) || a.organisation.localeCompare(b.organisation, 'en-GB'))
    .forEach((row, index) => { row.sentOrder = index + 1; });
  rows.sort(byDefaultOrder);
  unmatched.sort((a, b) => b.at.localeCompare(a.at));

  return {
    rows,
    unmatched,
    summary: {
      emailed: rows.length,
      replied: rows.filter((row) => row.repliedAt).length,
      bounced: rows.filter((row) => row.status === 'bounced').length,
      dueNow: rows.filter((row) => row.due === 'overdue' || row.due === 'today').length,
      finished: rows.filter((row) => row.sends >= 3 && !row.repliedAt).length,
      notYetEmailed: withAddress.filter((provider) => !emailed.has(provider)).length,
    },
  };
}

/** Follow-ups first (the oldest due first), then the providers that replied (newest reply first), then the rest by last email, newest first. */
function byDefaultOrder(a, b) {
  const group = (row) => (row.nextFollowUp ? 0 : row.repliedAt ? 1 : 2);
  if (group(a) !== group(b)) return group(a) - group(b);
  const time = (value) => Date.parse(value);
  const order = group(a) === 0
    ? time(a.nextFollowUp) - time(b.nextFollowUp)
    : group(a) === 1
      ? time(b.repliedAt) - time(a.repliedAt)
      : 0;
  return order || time(b.lastSentAt) - time(a.lastSentAt) || a.organisation.localeCompare(b.organisation, 'en-GB');
}
